import { deriveDocumentName, formatDocumentName } from "encamp-shared/src/documents";
import { DocumentType } from "generated-graphql/graphql";
import { useState, useMemo } from "react";

export interface FilenameStandardizationOptions {
  originalFilename?: string;
  documentType?: DocumentType;
  facilityName?: string;
  date?: Date;
  /** Whether this is an edit operation (hides checkbox) */
  isEdit?: boolean;
  /** Tenant id — used to look up per-tenant naming overrides. */
  tenantId?: string | null;
  /** Reporting year — used by per-tenant overrides that include `RY{year}` in the title. */
  reportingYear?: number | null;
}

export interface UseFilenameStandardizationReturn {
  /** The standardized filename based on current settings */
  standardizedFilename: string | undefined;
  /** Whether to keep the original filename */
  keepOriginalFilename: boolean;
  /** Function to toggle the keep original filename checkbox */
  setKeepOriginalFilename: (keep: boolean) => void;
  /** Whether the checkbox should be shown */
  shouldShowCheckbox: boolean;
}

/**
 * Hook for managing filename standardization across upload components.
 * Provides logic for standardizing filenames and managing the "Keep original filename" option.
 */
export function useFilenameStandardization(
  options: FilenameStandardizationOptions
): UseFilenameStandardizationReturn {
  const [keepOriginalFilename, setKeepOriginalFilename] = useState(false);

  const {
    originalFilename,
    documentType,
    facilityName,
    date = new Date(),
    isEdit = false,
    tenantId,
    reportingYear,
  } = options;

  // Check if this document type should be excluded from standardization
  const shouldExcludeFromStandardization = useMemo(() => {
    return !documentType || documentType === DocumentType.SafetyDataSheet;
  }, [documentType]);

  const shouldShowCheckbox = useMemo(() => {
    return !isEdit && !shouldExcludeFromStandardization;
  }, [isEdit, shouldExcludeFromStandardization]);

  const shouldStandardize = useMemo(() => {
    return !shouldExcludeFromStandardization && !keepOriginalFilename;
  }, [shouldExcludeFromStandardization, keepOriginalFilename]);

  const extension = useMemo(() => {
    return originalFilename?.split(".").pop();
  }, [originalFilename]);

  // Derive a cleaned "Document Name" segment from the user's filename so
  // per-tenant overrides (e.g. Dominion's `RY{Year}_DocumentName` format)
  // can preserve the user's naming intent instead of always falling back to
  // the document type. Tenants without an override ignore this value.
  const documentName = useMemo(() => {
    if (!originalFilename) return undefined;
    return deriveDocumentName({
      source: originalFilename,
      fileExtension: extension || "",
      facilityName: facilityName || "",
      reportingYear: reportingYear ?? null,
    });
  }, [originalFilename, extension, facilityName, reportingYear]);

  // Generate filename
  const standardizedFilename = useMemo(() => {
    if (!shouldStandardize || !documentType) {
      return originalFilename;
    }

    return formatDocumentName({
      document: {
        documentType,
        fileExtension: extension || "",
      },
      facilityName: facilityName || "",
      date,
      tenantId,
      reportingYear,
      documentName,
    });
  }, [
    documentType,
    facilityName,
    date,
    shouldStandardize,
    originalFilename,
    extension,
    tenantId,
    reportingYear,
    documentName,
  ]);

  return {
    standardizedFilename,
    keepOriginalFilename,
    setKeepOriginalFilename,
    shouldShowCheckbox,
  };
}
